<template>
  <div id="unitsMultipleForm">
    <v-overlay :value="loading" z-index="6">
      <v-progress-circular indeterminate size="64"/>
    </v-overlay>
    <v-row no-gutters justify="start">
      <v-col cols="12">
        <v-card class="pa-3">
          <v-card-title>
            <h4>{{$t($route.meta.breadcrumb[2].text)}}</h4>
          </v-card-title>
          <v-card-subtitle>
            <p>{{$t('units.multipleFormMessage')}}</p>
          </v-card-subtitle>
          <v-card-text>
            <v-row justify="center" v-if="!readingFile && units.length < 1">
              <v-col cols="12">
                <v-btn
                  color="primary"
                  depressed
                  :loading="loadingTemplate"
                  :disabled="loadingTemplate"
                  @click="downloadTemplate"
                >
                  <v-icon left>get_app</v-icon>{{$t('units.downloadTemplate')}}
                </v-btn>
              </v-col>
              <v-col cols="12">
                <div class="border-img">
                  <v-img
                    style="margin: 0 auto"
                    max-width="320px"
                    v-if="showImg"
                    :src="file.public_url">
                  </v-img>
                  <vue-dropzone
                    ref="dropzone"
                    id="dropzone"
                    :options="dropzoneOptions"
                    v-on:vdropzone-error="fileError"
                    v-on:vdropzone-success="fileUpload">
                  </vue-dropzone>
                </div>
              </v-col>
              <v-col class="text-right" cols="12">
                <v-btn class="mr-4" color="primary" outlined @click="redirect">{{$t('general.return')}}</v-btn>
              </v-col>
            </v-row>
            <v-row justify="center" v-if="readingFile">
              <v-col class="text-center">
                <v-progress-circular size="60" indeterminate color="primary"/>
                <p class="text-center mt-2">{{$t('general.loading')}}</p>
              </v-col>
            </v-row>
            <v-row justify="center" v-if="units.length > 0">
              <v-col cols="12">
                <h3 class="primary--text">{{$t('general.loadedElements')}}: </h3>
              </v-col>
              <v-col cols="12">
                <v-form
                  lazy-validation
                  ref="form"
                  v-model="valid"
                  class="pa-0 ma-0"
                >
                  <v-data-table
                    v-model="selected"
                    item-key="vin_number"
                    :items-per-page="-1"
                    :headers="headers"
                    :items="units"
                    show-select
                    hide-default-footer
                  >
                    <template v-slot:item="props">
                      <tr>
                        <td class="no-border">
                          <v-checkbox
                            :value="props.item"
                            v-model="selected"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-text-field
                            outlined
                            dense
                            hide-details="auto"
                            v-model="props.item.vin_number"
                            :error-messages="errors[props.index + '.vin_number']"
                            @focus="errors[props.index + '.vin_number'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-text-field
                            outlined
                            dense
                            hide-details="auto"
                            v-model="props.item.unit_number"
                            :error-messages="errors[props.index + '.unit_number']"
                            @focus="errors[props.index + '.unit_number'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-autocomplete
                            :items="properties"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="text"
                            v-model="props.item.owner"
                            :error-messages="errors[props.index + '.owner']"
                            @focus="errors[props.index + '.owner'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-text-field
                            :disabled="props.item.owner === 'OWN'"
                            outlined
                            dense
                            hide-details="auto"
                            v-model="props.item.owner_name"
                            :error-messages="errors[props.index + '.owner_name']"
                            @focus="errors[props.index + '.owner_name'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-autocomplete
                            :items="unitTypes"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="name"
                            v-model="props.item.unit_types"
                            :error-messages="errors[props.index + '.unit_types']"
                            @focus="errors[props.index + '.unit_types'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-autocomplete
                            :items="sites"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="name"
                            v-model="props.item.yard"
                            :error-messages="errors[props.index + '.yard']"
                            @focus="errors[props.index + '.yard'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-text-field
                            outlined
                            dense
                            hide-details="auto"
                            v-model="props.item.plate_1"
                            :error-messages="errors[props.index + '.plate_1']"
                            @focus="errors[props.index + '.plate_1'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-autocomplete
                            :items="countries"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="name"
                            v-model="props.item.country_plate_1"
                            :error-messages="errors[props.index + '.country_plate_1']"
                            @focus="errors[props.index + '.country_plate_1'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-text-field
                            outlined
                            dense
                            hide-details="auto"
                            v-model="props.item.plate_2"
                            :error-messages="errors[props.index + '.plate_2']"
                            @focus="errors[props.index + '.plate_2'] = null"
                          />
                        </td>
                        <td class="no-border px-2">
                          <v-autocomplete
                            :items="countries"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="name"
                            v-model="props.item.country_plate_2"
                            :error-messages="errors[props.index + '.country_plate_2']"
                            @focus="errors[props.index + '.country_plate_2'] = null"
                          />
                        </td>
                      </tr>
                      <tr style="background-color: #fff">
                        <td></td>
                        <td colspan="12">
                          <v-row dense>
                            <v-col cols="2">
                              <h5>{{$t('general.brand')}}:</h5>
                              <v-text-field
                                class="mb-4"
                                outlined
                                dense
                                hide-details="auto"
                                v-model="props.item.brand"/>
                            </v-col>
                            <v-col cols="2">
                              <h5>{{$t('general.model')}}:</h5>
                              <v-text-field
                                class="mb-4"
                                outlined
                                dense
                                hide-details="auto"
                                v-model="props.item.model" />
                            </v-col>
                            <v-col cols="8">
                              <h5>{{$t('general.comment')}}:</h5>
                              <v-text-field
                                class="mb-4"
                                outlined
                                dense
                                hide-details="auto"
                                v-model="props.item.comment" />
                            </v-col>
                          </v-row>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-form>
              </v-col>
              <v-col class="text-right" cols="12">
                <v-btn class="mr-4" color="primary" outlined @click="redirect">{{$t('general.return')}}</v-btn>
                <v-btn class="mr-4" color="primary" outlined @click="limpiarCarga">{{$t('general.reloadFile')}}</v-btn>
                <v-btn
                  color="primary" 
                  depressed 
                  :loading="saveLoading"
                  :disabled="selected.length < 1" 
                  @click="saveUnits"
                >{{$t('general.save')}}</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
export default {
  name: 'unitsMultipleForm',
  components: {
    vueDropzone: vue2Dropzone
  },
  data () {
    return {
      loading: false,
      loadingTemplate: false,
      saveLoading: false,
      errors: [],
      valid: true,
      showImg: false,
      readingFile: false,
      countries: [],
      unitTypes: [],
      sites: [],
      selected: [],
      units: [],
      properties: [{id: 1,text: 'OWN'}, {id: 2,text: 'EXTERNAL'}],
      file: {
        id: '',
      },
      options: {
        page: 1,
        itemsPerPage: 20,
      },
      dropzoneOptions: {
        url: process.env.VUE_APP_HOST + 'loading-unit-masive/import',
        dictDefaultMessage: `<p class="text-center mb-5">${this.$t('dropzone.default', { size: '15', unit: 'MB' })}</p><h1 class="text-center mb-5">
        <i class="fas fa-cloud-upload-alt"></i></h1><p class="text-center">${this.$t('dropzone.fileFormat', {formats: '.xlsx'})}</p>`,
        dictResponseError: this.$t('dropzone.responseError'),
        dictFileTooBig: this.$t('dropzone.fileTooBig'),
        dictInvalidFileType: this.$t('dropzone.invalidFileType'),
        dictRemoveFile: this.$t('dropzone.removeFile'),
        dictRemoveFileConfirmation: this.$t('dropzone.removeFileConfirmation'),
        dictMaxFilesExceeded: this.$t('dropzone.maxFilesExceeded'),
        uploadMultiple: false,
        addRemoveLinks: true,
        maxFilesize: 15,
        paramName: 'file',
        // params: {
        //   'is_public': 1
        // },
        acceptedFiles: '.xlsx',
        maxFiles: 1,
        headers: {
          'Authorization': 'Bearer ' + this.$store.state.userState.access_token,
          'Cache-Control': null,
          'X-Requested-With': null
        }
      },
    }
  },
  created () {
    this.$store.dispatch('unitTypes/getAll', {event: {context: this}})
    this.$store.dispatch('sites/get', {event: {item: {perPage: -1, page: 1}, context: this}})
    this.$store.dispatch('getCountry', {event: {context: this}})
  },
  watch: {
    // countries (val) {
    //   if (val.length > 0) {
    //     val.forEach(element => {
    //       const acentos = {'á':'a','é':'e','í':'i','ó':'o','ú':'u','Á':'A','É':'E','Í':'I','Ó':'O','Ú':'U'}
    //       element.name = element.name.split('').map( letra => acentos[letra] || letra).join('').toString()
    //       element.name = element.name.trim()
    //       // element.name = element.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    //     })
    //   }
    // }
  },
  computed: {
    rules () {
      return this.$validationRules()
    },
    headers () {
      return [
        {
          text: 'VIN',
          value: 'vin_number',
          sortable: true
        },
        {
          text: this.$t('general.unitNumber'),
          sortable: false
        },
        {
          text: this.$t('general.property'),
          sortable: false
        },
        {
          text: this.$t('general.ownerName'),
          sortable: false
        },
        {
          text: this.$t('general.unitType'),
          sortable: false
        },
        {
          text: this.$t('general.site'),
          sortable: false
        },
        {
          text: this.$t('general.plates'),
          sortable: false
        },
        {
          text: this.$t('address.country'),
          sortable: false
        },
        {
          text: this.$t('general.plates') + ' 2',
          sortable: false
        },
        {
          text: this.$t('address.country') + ' 2',
          sortable: false
        }
      ]
    }
  },
  methods: {
    fileUpload (file, response) {
      this.units = response.data.units
      this.showNotification(this.$t('dropzone.logoSuccess'), 'success')
      // this.activate()
    },
    limpiarCarga () {
      this.units = []
      this.selected = []
    },
    fileError (file, message) {
      this.units = message.data
      this.errors = message.errors
      this.showNotification(message.message, 'error')
    },
    removed () {
      this.file.id = undefined
    },
    activate () {
      this.readingFile = true
      this.$store.dispatch('units/importUnits', {event: { id: this.file.id ,context: this}})
    },
    saveUnits () {
      if (this.selected.length > 0) {
        this.loading = true
        if (this.$refs.form.validate()) {
          this.$store.dispatch('units/saveUnits', {event: {items: this.selected, context: this}})
        } else {
          this.loading = false
        }
      }
    },
    downloadTemplate () {
      this.loadingTemplate = true
      this.$store.dispatch('units/downloadUnitsTemplate', {event: {context: this}})
    },
    redirect () {
      this.$router.push({name: 'Units'})
    },
  }
}
</script>
<style>
  .dz-error-message {
    display: none !important;
  }
  #dropzone {
    display: flex;
    justify-content: center;
    margin-top: 0;
    border-radius: 5px;
  }
  .border-img {
    border: 1px solid #9E9E9E;
    border-radius: 5px;
  }
  .no-border {
    border-bottom: none !important;
  }
</style>